import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Chip,
  Tooltip,
  Avatar,
  Divider,
  alpha,
  Switch,
  FormControlLabel,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  FormHelperText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import WorkIcon from '@mui/icons-material/Work';
import CategoryIcon from '@mui/icons-material/Category';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme, status }) => ({
  position: 'relative',
  marginBottom: theme.spacing(2),
  borderRadius: 1,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  height: '100px',
  minHeight: '100px',
  maxHeight: '100px',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1.75),
  gap: theme.spacing(0.75),
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.25),
    '& .action-buttons': {
      opacity: 1
    }
  }
}));

const StatusIndicator = styled('div')(({ theme, status }) => ({
  position: 'absolute',
  bottom: theme.spacing(2.5),
  right: theme.spacing(4.6),
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: status === 'pending' 
    ? theme.palette.warning.main 
    : theme.palette.success.main
}));

const RoleChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  color: theme.palette.primary.dark,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  fontWeight: 500,
  fontSize: '0.7rem',
  height: 22,
  letterSpacing: 0.3,
  '& .MuiChip-icon': {
    fontSize: 14
  }
}));

const DepartmentChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.secondary.main, 0.06),
  color: theme.palette.secondary.dark,
  fontWeight: 500,
  fontSize: '0.75rem',
  height: '24px',
  '& .MuiChip-icon': {
    color: theme.palette.secondary.main
  }
}));

const AdminChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.error.main, 0.1),
  color: theme.palette.error.main,
  fontWeight: 500,
  fontSize: '0.75rem',
  height: '24px',
  '& .MuiChip-icon': {
    color: theme.palette.error.main
  }
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.error.main, 0.08),
  color: theme.palette.error.main,
  opacity: 0,
  transform: 'translateX(10px)',
  border: `1px solid ${alpha(theme.palette.error.main, 0.15)}`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.error.main, 0.15)
  }
}));

const UserAvatar = styled(Avatar)(({ theme, status }) => ({
  width: 40,
  height: 40,
  marginRight: theme.spacing(1.5),
  backgroundColor: status === 'pending' ? theme.palette.warning.main : theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: 600,
  fontSize: '0.9rem'
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    color: theme.palette.primary.main
  }
}));

// Department-specific roles mapping (aligned with CrewManagement.js and backend)
const DEPARTMENT_ROLES = {
  'direction': [
    'director',
    'first-assistant-director',
    'second-assistant-director',
    'second-second-ad',
    'script-supervisor',
    'additional-assistant-director',
    'director-pa'
  ],
  'production': [
    'executive-producer',
    'producer',
    'line-producer',
    'unit-production-manager',
    'production-supervisor',
    'production-coordinator',
    'production-secretary',
    'production-accountant',
    'production-assistant',
    'set-pa'
  ],
  'camera': [
    'director-of-photography',
    'cinematographer',
    'camera-operator',
    'first-assistant-camera',
    'second-assistant-camera',
    'loader',
    'camera-utility',
    'dit',
    'steadicam-operator',
    'camera-pa'
  ],
  'electric': [
    'gaffer',
    'best-boy-electric',
    'electric',
    'genny-operator',
    'board-operator',
    'rigging-electric',
    'electric-utility',
    'electric-pa'
  ],
  'grip': [
    'key-grip',
    'best-boy-grip',
    'grip',
    'dolly-grip',
    'rigging-grip',
    'grip-utility',
    'grip-pa'
  ],
  'sound': [
    'production-sound-mixer',
    'sound-supervisor',
    'boom-operator',
    'sound-utility',
    'sound-pa'
  ],
  'art': [
    'production-designer',
    'art-director',
    'set-decorator',
    'prop-master',
    'set-dresser',
    'art-pa'
  ],
  'makeup': [
    'makeup-department-head',
    'key-makeup-artist',
    'makeup-artist',
    'special-effects-makeup',
    'makeup-pa'
  ],
  'hair': [
    'hair-department-head',
    'key-hair-stylist',
    'hair-stylist',
    'hair-pa'
  ],
  'costume': [
    'costume-designer',
    'assistant-costume-designer',
    'costume-supervisor',
    'set-costumer',
    'costume-pa'
  ],
  'post-production': [
    'post-supervisor',
    'editor',
    'colorist'
  ],
  'vfx': [
    'vfx-supervisor',
    'vfx-artist'
  ],
  'location': [
    'location-manager',
    'location-scout'
  ],
  'transportation': [
    'transportation-coordinator',
    'transportation-captain',
    'driver',
    'transportation-pa'
  ],
  'craft-services': [
    'craft-services-lead',
    'craft-services-assistant'
  ],
  'catering': [
    'caterer',
    'catering-assistant'
  ],
  'construction': [
    'construction-coordinator',
    'construction-foreman',
    'carpenter',
    'scenic',
    'construction-pa'
  ],
  'greens': [
    'greensmaster',
    'assistant-greens',
    'greens-person'
  ],
  'stunts': [
    'stunt-coordinator',
    'stunt-performer',
    'stunt-rigger'
  ],
  'special-effects': [
    'sfx-supervisor',
    'sfx-technician',
    'pyrotechnician'
  ],
  'accounting': [
    'assistant-accountant',
    'payroll-accountant'
  ],
  'security': [
    'security-coordinator',
    'security-guard'
  ],
  'medical': [
    'set-medic'
  ],
  'publicity': [
    'publicist',
    'unit-publicist'
  ],
  'other': ['other']
};

// Valid departments (aligned with CrewManagement.js and backend)
const VALID_DEPARTMENTS = [
  'direction',
  'production',
  'camera',
  'electric',
  'grip',
  'sound',
  'art',
  'makeup',
  'hair',
  'costume',
  'post-production',
  'vfx',
  'location',
  'transportation',
  'craft-services',
  'catering',
  'construction',
  'greens',
  'stunts',
  'special-effects',
  'accounting',
  'security',
  'medical',
  'publicity',
  'other'
];

const EditDialog = ({ open, onClose, crewMember, onSave }) => {
  const [role, setRole] = useState(crewMember?.role || '');
  const [department, setDepartment] = useState(crewMember?.department || '');
  
  // Get available roles for the selected department
  const availableRoles = department ? DEPARTMENT_ROLES[department] || [] : [];
  
  // Helper function to format role/department names for display
  const formatName = (name) => {
    return name.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  
  // Reset role when department changes
  const handleDepartmentChange = (newDepartment) => {
    setDepartment(newDepartment);
    setRole(''); // Reset role when department changes
  };
  
  const handleSave = () => {
    onSave({
      ...crewMember,
      role,
      department
    });
    onClose();
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Crew Member Position</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Department</InputLabel>
            <Select
              value={department}
              label="Department"
              onChange={(e) => handleDepartmentChange(e.target.value)}
            >
              {VALID_DEPARTMENTS.map((d) => (
                <MenuItem key={d} value={d}>
                  {formatName(d)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth disabled={!department}>
            <InputLabel>Role</InputLabel>
            <Select
              value={role}
              label="Role"
              onChange={(e) => setRole(e.target.value)}
            >
              {availableRoles.map((r) => (
                <MenuItem key={r} value={r}>
                  {formatName(r)}
                </MenuItem>
              ))}
            </Select>
            {!department && (
              <FormHelperText>Select a department first</FormHelperText>
            )}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!department || !role}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CrewCard = ({ crewMember, onDelete, onToggleAdmin, onEdit, onManagePermissions, currentUserIsAdmin = false, isProjectOwner = false, isLastAdmin = false, effectiveIsAdmin = undefined }) => {
  const theme = useTheme();
  const { user, role, department, status } = crewMember;
  const [isAdmin, setIsAdmin] = useState(crewMember.isAdmin || false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const getStatusIcon = () => {
    return status === 'pending' ? <AccessTimeIcon /> : <CheckCircleIcon />;
  };

  const getStatusLabel = () => {
    return status === 'pending' ? 'Pending Acceptance' : 'Active';
  };

  const formatName = (text) => {
    return text.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Get initials for avatar
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  
  // Update local state when props change (prefer effectiveIsAdmin if provided)
  useEffect(() => {
    if (typeof effectiveIsAdmin === 'boolean') {
      setIsAdmin(effectiveIsAdmin);
    } else {
      setIsAdmin(crewMember.isAdmin || false);
    }
  }, [crewMember, effectiveIsAdmin]);

  const handleAdminToggle = async (e) => {
    e.stopPropagation();
    if (isProjectOwner && isAdmin) return;
    
    try {
      const newAdminStatus = !isAdmin;
      // If demoting and this is the last admin, ask for confirmation
      if (!newAdminStatus && isLastAdmin) {
        setConfirmOpen(true);
        return;
      }
      setToggling(true);
      setIsAdmin(newAdminStatus);
      await onToggleAdmin(crewMember._id, newAdminStatus);
      handleMenuClose(); // Close menu after action
    } catch (error) {
      console.error('Error toggling admin status:', error);
      setIsAdmin(isAdmin); // Revert on error
    }
    finally {
      setToggling(false);
    }
  };
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleEdit = () => {
    setEditDialogOpen(true);
    handleMenuClose();
  };
  
  const handleDelete = () => {
    onDelete(crewMember._id);
    handleMenuClose();
  };
  
  const handleEditSave = (updatedMember) => {
    if (onEdit) {
      onEdit(updatedMember);
    }
  };

  return (
    <>
      <StyledCard status={status}>
        <CardContent sx={{ 
          p: 1.5, 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center',
          py: 0
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar 
                sx={{ 
                  width: 36, 
                  height: 36,
                  fontSize: '0.85rem',
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText
                }}
              >
                {getInitials(user.name)}
              </Avatar>
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography 
                    variant="subtitle1"
                    sx={{ 
                      fontWeight: 600, 
                      lineHeight: 1.3,
                      letterSpacing: 0.1,
                      color: theme.palette.text.primary,
                      mb: 0.25
                    }}
                  >
                    {user.name}
                  </Typography>
                  {(isAdmin || (isProjectOwner && status !== 'pending')) && (
                    <AdminChip 
                      icon={<AdminPanelSettingsIcon fontSize="small" />} 
                      label={isProjectOwner ? 'Owner' : 'Admin'}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                    letterSpacing: 0.15,
                    mb: 0.25
                  }}
                >
                  {user.email}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                    letterSpacing: 0.15
                  }}
                >
                  {user.position}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                className="action-buttons"
                sx={{ 
                  opacity: 0.8,
                  transition: 'opacity 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.action.active, 0.08)
                  }
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <DepartmentChip 
              icon={<WorkIcon fontSize="small" />}
              label={department}
              size="small"
              sx={{ 
                '& .MuiChip-icon': {
                  color: theme.palette.secondary.main,
                  marginLeft: '4px'
                }
              }}
            />
            <RoleChip 
              icon={<AdminPanelSettingsIcon fontSize="small" />} 
              label={role}
              size="small"
              sx={{ 
                '& .MuiChip-icon': {
                  color: theme.palette.primary.main,
                  marginLeft: '4px'
                }
              }}
            />
          </Box>
        </CardContent>
        <StatusIndicator 
          status={status}
        />
      </StyledCard>
      
      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Position</ListItemText>
        </MenuItem>
        {currentUserIsAdmin && status !== 'pending' && (
          <MenuItem onClick={() => { onManagePermissions && onManagePermissions(crewMember); handleMenuClose(); }}>
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Role & Permissions</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Remove</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Edit Dialog */}
      <EditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        crewMember={crewMember}
        onSave={handleEditSave}
      />

      {/* Confirm demote last admin */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Remove last admin?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This is the last admin on the project. Removing admin requires that at least one other member is an admin.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              setConfirmOpen(false);
              // Proceed anyway; server-side should block if policy forbids.
              try {
                setToggling(true);
                setIsAdmin(false);
                await onToggleAdmin(crewMember._id, false);
                handleMenuClose();
              } catch (err) {
                setIsAdmin(true);
              } finally {
                setToggling(false);
              }
            }}
          >
            Remove Admin
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CrewCard;
